import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Badge, Button, DataTable, FilterChips, PageHeader, type ColumnDef, type FilterChipOption } from '@lipaflow/ui'
import { adminService, type CurriculumDocument, type CurriculumEdition } from '../services/admin'
import { usePageTitle } from '../lib/pageTitle'
import { AddDocumentModal, DocumentPanel, EditionPanel, NewEditionModal } from './curriculum/CurriculumPanels'
import { DOCUMENT_STATUS, EDITION_STATUS } from './curriculum/labels'

type View = 'editions' | 'documents'

/**
 * Curriculum review — turning curriculum PDFs into the learning outcomes
 * teachers plan with. Laid out as the journey it is (add documents, extract
 * into a draft edition, check, make live) instead of five forms at once.
 */
export default function CurriculumQaPage() {
  usePageTitle('Curriculum review')
  const [view, setView] = useState<View>('editions')
  const [docStatus, setDocStatus] = useState('')
  const [edition, setEdition] = useState<CurriculumEdition | null>(null)
  const [document, setDocument] = useState<CurriculumDocument | null>(null)
  const [adding, setAdding] = useState(false)
  const [creating, setCreating] = useState(false)

  const documents = useQuery({
    queryKey: ['curriculum-v2', 'documents', docStatus],
    queryFn: () => adminService.listCurriculumV2Documents({ limit: 50, status: docStatus || undefined }),
  })
  const allDocuments = useQuery({
    queryKey: ['curriculum-v2', 'documents', 'all'],
    queryFn: () => adminService.listCurriculumV2Documents({ limit: 200 }),
  })
  const editions = useQuery({
    queryKey: ['curriculum-v2', 'editions'],
    queryFn: () => adminService.listCurriculumV2Editions({ limit: 50 }),
  })

  const eds = editions.data ?? []
  const docs = allDocuments.data ?? []
  const ready = docs.filter((d) => d.status === 'uploaded').length
  const drafts = eds.filter((e) => e.status === 'draft').length
  const live = eds.filter((e) => e.status === 'active').length

  const views: FilterChipOption<View>[] = [
    { key: 'editions', label: 'Editions', count: eds.length },
    { key: 'documents', label: 'Source documents', count: docs.length },
  ]

  const editionColumns: ColumnDef<CurriculumEdition>[] = [
    {
      key: 'title',
      header: 'Edition',
      cell: (e) => (
        <div>
          <div className="font-medium text-ink">{e.title || e.version_tag}</div>
          <div className="text-caption text-ink-muted font-mono">{e.curriculum_code} · {e.version_tag}</div>
        </div>
      ),
    },
    { key: 'year', header: 'Year', hideOnMobile: true, cell: (e) => <span className="text-body-sm text-ink-muted">{e.published_year ?? '—'}</span> },
    {
      key: 'status',
      header: 'Status',
      cell: (e) => {
        const s = EDITION_STATUS[e.status] ?? { label: e.status, variant: 'neutral' as const }
        return <Badge variant={s.variant} size="sm">{s.label}</Badge>
      },
    },
  ]
  const documentColumns: ColumnDef<CurriculumDocument>[] = [
    {
      key: 'title',
      header: 'Document',
      cell: (d) => (
        <div>
          <div className="font-medium text-ink">{d.title}</div>
          <div className="text-caption text-ink-muted font-mono">{d.curriculum_code}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (d) => {
        const s = DOCUMENT_STATUS[d.status] ?? { label: d.status, variant: 'neutral' as const }
        return <Badge variant={s.variant} size="sm">{s.label}</Badge>
      },
    },
    {
      key: 'added',
      header: 'Added',
      hideOnMobile: true,
      cell: (d) => <span className="text-caption text-ink-muted">{new Date(d.created_at).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}</span>,
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Curriculum review"
        subtitle="Turn curriculum PDFs into the learning outcomes teachers plan with."
        actions={
          <>
            <Link to="../curriculum-library" className="inline-flex"><Button variant="ghost">Open the library</Button></Link>
            <Button variant="secondary" onClick={() => setCreating(true)}>New edition</Button>
            <Button leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setAdding(true)}>Add a document</Button>
          </>
        }
      />

      <ol className="grid grid-cols-2 lg:grid-cols-4 gap-3" aria-label="How a curriculum goes live">
        <Journey n={1} title="Add documents" value={`${ready} ready to extract`} />
        <Journey n={2} title="Extract into a draft edition" value={`${drafts} ${drafts === 1 ? 'draft' : 'drafts'}`} />
        <Journey n={3} title="Check it in the library" value="Read what was extracted" />
        <Journey n={4} title="Make it live" value={`${live} live`} />
      </ol>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <FilterChips options={views} value={view} onChange={setView} />
        {view === 'documents' && (
          <select
            aria-label="Document status"
            value={docStatus}
            onChange={(e) => setDocStatus(e.target.value)}
            className="rounded-button border border-hairline bg-surface p-2 text-body-sm text-ink"
          >
            <option value="">All statuses</option>
            {Object.entries(DOCUMENT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        )}
      </div>

      {view === 'editions' ? (
        <DataTable
          data={eds}
          columns={editionColumns}
          getRowId={(e) => e.id}
          loading={editions.isLoading}
          onRowClick={setEdition}
          emptyState={{
            title: 'No editions yet',
            description: 'Create a draft edition, then extract documents into it.',
            action: <Button onClick={() => setCreating(true)}>New edition</Button>,
          }}
        />
      ) : (
        <DataTable
          data={documents.data ?? []}
          columns={documentColumns}
          getRowId={(d) => d.id}
          loading={documents.isLoading}
          onRowClick={setDocument}
          emptyState={{
            title: docStatus ? 'No documents with that status' : 'No documents yet',
            description: 'Upload a curriculum PDF to get started.',
            action: <Button onClick={() => setAdding(true)}>Add a document</Button>,
          }}
        />
      )}

      <EditionPanel edition={edition} documents={docs} editions={eds} onClose={() => setEdition(null)} />
      <DocumentPanel document={document} onClose={() => setDocument(null)} />
      <AddDocumentModal open={adding} onClose={() => setAdding(false)} />
      <NewEditionModal open={creating} onClose={() => setCreating(false)} />
    </div>
  )
}

function Journey({ n, title, value }: { n: number; title: string; value: string }) {
  return (
    <li className="rounded-card border border-hairline bg-surface p-4">
      <span className="text-caption text-ink-faint">Step {n}</span>
      <span className="block text-body-sm font-medium text-ink">{title}</span>
      <span className="block text-caption text-ink-muted mt-1 tabular">{value}</span>
    </li>
  )
}
